/* OpenClip craft homepage — module behaviour.
 *
 * Scope: only inside a [data-oc-section] root. Nothing global, nothing that can reach the PDP.
 * MD §4: JS 用模块初始化与清理函数；处理 shopify:section:load/unload 等编辑器生命周期，
 *        避免事件重复绑定；只在自身实例内查找元素。
 *
 * Every listener is registered with an AbortController so a section can be torn down cleanly
 * when the theme editor reloads or removes it. Re-initialising an already-initialised root is a
 * no-op, so the editor cannot stack duplicate handlers.
 */
(function () {
  'use strict';

  var READY = 'data-oc-ready';

  /* Cross-section registry: oc-design-themes needs to drive the oc-featured-products filter,
     and each section is a separate instance with its own section.id. */
  window.__ocHome = window.__ocHome || { shelves: {} };

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function initScope(scope) {
    if (!scope || scope.hasAttribute(READY)) return;
    scope.setAttribute(READY, '');

    var controller = new AbortController();
    var signal = controller.signal;
    scope.__ocController = controller;

    function one(sel, root) { return (root || scope).querySelector(sel); }
    function all(sel, root) { return toArray((root || scope).querySelectorAll(sel)); }

    /* ---------------------------------------------------------------- mobile menu */
    var menuBtn = one('.menubtn');
    var menu = one('.mobilemenu');
    if (menuBtn && menu) {
      function setMenu(open, returnFocus) {
        menu.classList.toggle('open', open);
        menuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        menuBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        if (!open && returnFocus) menuBtn.focus();
      }
      menuBtn.addEventListener('click', function () {
        setMenu(!menu.classList.contains('open'), false);
      }, { signal: signal });
      menu.addEventListener('click', function (event) {
        /* MD §3: 选链接后关闭 */
        if (event.target.closest('a')) setMenu(false, false);
      }, { signal: signal });
      document.addEventListener('keydown', function (event) {
        /* MD §3: Escape 关闭并返焦点 */
        if (event.key === 'Escape' && menu.classList.contains('open')) setMenu(false, true);
      }, { signal: signal });
    }

    /* ---------------------------------------------------------------- product filter */
    var shelf = one('.products');
    var cards = all('[data-oc-product]');
    var filterButtons = all('[data-oc-filter]');
    var empty = one('[data-oc-empty]');

    function applyFilter(choice) {
      var shown = 0;
      cards.forEach(function (card) {
        var group = card.getAttribute('data-filter-group') || '';
        var visible = choice === 'all' || group === choice;
        card.hidden = !visible;
        if (visible) shown += 1;
      });
      filterButtons.forEach(function (button) {
        var active = button.getAttribute('data-oc-filter') === choice;
        button.classList.toggle('active', active);
        /* MD §3: 选中状态酒红底白字，aria-pressed 同步 */
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (empty) empty.hidden = shown > 0;
      /* MD §3: 切换后横滑位置回到起点 */
      if (shelf) shelf.scrollLeft = 0;
    }

    if (cards.length) {
      filterButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          applyFilter(button.getAttribute('data-oc-filter') || 'all');
        }, { signal: signal });
      });
      /* Register so oc-design-themes can drive this shelf. */
      window.__ocHome.shelves[scope.id] = { applyFilter: applyFilter, element: scope };
      scope.__ocShelfKey = scope.id;
      applyFilter('all');
    }

    /* ---------------------------------------------------------------- design-theme cards */
    /* MD §3/07: 点击先应用商品筛选，再滚动到 #projects；不保留假交互。 */
    all('[data-oc-theme-filter]').forEach(function (card) {
      card.addEventListener('click', function (event) {
        var targetId = card.getAttribute('data-oc-filter-target');
        var key = card.getAttribute('data-oc-theme-filter');
        var shelfEntry = window.__ocHome.shelves[targetId];
        if (!shelfEntry) return; /* section hidden or missing: fall through to a plain link */
        event.preventDefault();
        shelfEntry.applyFilter(key);
        shelfEntry.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, { signal: signal });
    });

    /* ---------------------------------------------------------------- videos */
    /* MD §3/05: controls/muted/playsinline/preload none；初始化 defaultMuted/muted true；
       用户主动解除静音后不在每次 play 强行重置；同页另一条播放时暂停上一条。 */
    var videos = all('video');
    if (videos.length) {
      videos.forEach(function (video) {
        video.defaultMuted = true;
        video.muted = true;
        video.addEventListener('play', function () {
          videos.forEach(function (other) {
            if (other !== video && !other.paused) other.pause();
          });
        }, { signal: signal });
        video.addEventListener('error', function () {
          var figure = video.closest('figure');
          if (figure) figure.classList.add('oc-video-failed');
        }, { signal: signal });
      });
    }
  }

  function teardownScope(scope) {
    if (!scope) return;
    if (scope.__ocController) {
      scope.__ocController.abort();
      scope.__ocController = null;
    }
    if (scope.__ocShelfKey && window.__ocHome.shelves[scope.__ocShelfKey]) {
      delete window.__ocHome.shelves[scope.__ocShelfKey];
      scope.__ocShelfKey = null;
    }
    scope.removeAttribute(READY);
  }

  function scopeFrom(node) {
    if (!node || node.nodeType !== 1) return null;
    if (node.matches('[data-oc-section]')) return node;
    return node.querySelector('[data-oc-section]');
  }

  function initAll(root) {
    toArray((root || document).querySelectorAll('[data-oc-section]')).forEach(initScope);
  }

  /* Theme-editor lifecycle (MD §4). Also covers the storefront first paint. */
  document.addEventListener('shopify:section:load', function (event) {
    var scope = scopeFrom(event.target);
    if (scope) initScope(scope);
    else initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    toArray(event.target.querySelectorAll('[data-oc-section]')).forEach(teardownScope);
    if (event.target.matches && event.target.matches('[data-oc-section]')) teardownScope(event.target);
  });

  /* Block re-selection must re-init because the DOM is replaced. */
  document.addEventListener('shopify:block:select', function (event) {
    var scope = scopeFrom(event.target);
    if (scope) {
      teardownScope(scope);
      initScope(scope);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
})();
